import React from "react";
import "./MyAccordion.css";
import {Glob as G} from "../libs/Glob.mjs";

const baseClassName = 'MyAccordion';

const MyBullet = class extends React.Component {
	render() {
		return (
			<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" fill="currentColor" stroke="none" style={this.props.style||{}}>
				<path d="M24.8175,16.86432,9.503,25.77667A1,1,0,0,1,8,24.91235V7.08765a1,1,0,0,1,1.503-.86432L24.8175,15.13568A1.00006,1.00006,0,0,1,24.8175,16.86432Z"/>
			</svg>
		);
	}
}

/**
 * @typedef MyAccordionProps
 * @property {JSX.Element|string} title The title to be displayed in accordion header
 * @property {JSX.Element} [children] The content of accordion body
 * @property {JSX.Element|string} [bullet] The bullet of the acordion toggler
 * @property {object} [headerStyle] Optional additional style rules for header
 * @property {(elem:HTMLElement)=>void} [onBeforeOpen] A function to be invoked before opening the accordion, input: reference to the main Accordion DIV
 * @property {(elem:HTMLElement)=>void} [onBeforeClose] A function to be invoked before closing the accordion, input: reference to the main Accordion DIV
 * @property {(elem:HTMLElement)=>void} [onOpen] A function to be invoked after opening the accordion, input: reference to the main Accordion DIV
 * @property {(elem:HTMLElement)=>void} [onClose] A function to be invoked after closing the accordion, input: reference to the main Accordion DIV
 * @property {(elem:HTMLElement, mode:boolean)=>void} [onToggle] A function to be invoked after opening/closing the accordion, input: reference to the main Accordion DIV, open state boolean
 */

/**
 * @typedef MyAccordionState
 * @property {boolean} open Is the accordion open?
 */

/**
 * @typedef MyAccordionNode
 * @type {JSX.Element}
 * @property {JSX.Element|string} title The title to be displayed in accordion header
 * @property {JSX.Element} [children] The content of accordion body
 * @property {JSX.Element|string} [bullet] The bullet of the acordion toggler
 * @property {object} [headerStyle] Optional additional style rules for header
 * @property {(elem:HTMLElement)=>void} [onBeforeOpen] A function to be invoked before opening the accordion, input: reference to the main Accordion DIV
 * @property {(elem:HTMLElement)=>void} [onBeforeClose] A function to be invoked before closing the accordion, input: reference to the main Accordion DIV
 * @property {(elem:HTMLElement)=>void} [onOpen] A function to be invoked after opening the accordion, input: reference to the main Accordion DIV
 * @property {(elem:HTMLElement)=>void} [onClose] A function to be invoked after closing the accordion, input: reference to the main Accordion DIV
 * @property {(elem:HTMLElement, mode:boolean)=>void} [onToggle] A function to be invoked after opening/closing the accordion, input: reference to the main Accordion DIV, open state boolean
 */

/**
 * @type {MyAccordionNode}
 */
const MyAccordion = class extends React.Component {
	/**
	 * @constructor
	 * @param { MyAccordionProps } props
	 * @return {MyAccordionNode}
	 */
	constructor(props) {
		super(props);
		/** @type {MyAccordionState } */
		this.state = { open: !!props.open };
		this.toggle = this.toggle.bind(this);
		this.uid = G.generateUid(baseClassName);
	}

	/**
	 * @param {boolean} mode Is the accordion to be opened?
	 */
	toggle(mode) {
		if (typeof mode === 'undefined') mode = !this.state.open;
		const o = window.document.getElementById(this.uid);
		if ( mode && typeof this.props.onBeforeOpen === 'function' ) {
			this.props.onBeforeOpen( o );
		} else if ( ! mode && typeof this.props.onBeforeClose === 'function' ) {
			this.props.onBeforeClose( o );
		}
		this.setState({open: mode}, () =>{
			if ( mode && typeof this.props.onOpen === 'function' ) {
				this.props.onOpen( o );
			} else if ( ! mode && typeof this.props.onClose === 'function' ) {
				this.props.onClose( o );
			}
			if ( typeof this.props.onToggle === 'function' ) {
				this.props.onToggle( o, !! mode );
			}
		});
	}

	render() {
		return (
			<div
				className={baseClassName + ' ' + (this.state.open ? 'expanded' : 'collapsed')}
				id={this.uid}
			>
				<div
					className={baseClassName + 'Header'}
					style={ this.props.headerStyle || {}}
				>
					<div
						key={this.state.bullet}
						className={baseClassName + 'Toggler'}
						onClick={ ()=>{ this.toggle() } }
					>
						{ this.props.bullet ?? <MyBullet /> }
					</div>
					<div
						key={String(this.props.title)}
						className={baseClassName + 'Title'}
						onClick={ ()=>{ this.toggle() } }
					>
						{this.props.title}
					</div>
				</div>
				<div className={baseClassName + 'Body'}>
					<div className={baseClassName + 'InnerBody'}>
						{ this.props.children }
					</div>
				</div>
			</div>
		);
	}
}

export default MyAccordion;